import { forwardRef, PropsWithChildren, ReactNode } from 'react';
import {
    SnackbarProvider as NotistackProvider,
    SnackbarProviderProps as NotistackProviderProps,
    CustomContentProps,
    closeSnackbar,
} from 'notistack';
import { SnackbarContent } from './SnackbarContent';

// Extend CustomContentProps to include our custom props
interface ExtendedContentProps extends CustomContentProps {
    icon?: ReactNode;
    description?: ReactNode;
    layout?: 'inline' | 'stacked';
}

/**
 * Custom snackbar component that adapts notistack's CustomContentProps to our SnackbarContent.
 */
const CustomSnackbar = forwardRef<HTMLDivElement, ExtendedContentProps>(
    function CustomSnackbar(props, ref) {
        const { id, message, variant, icon, description, layout } = props;

        const handleClose = () => {
            closeSnackbar(id);
        };

        return (
            <SnackbarContent
                ref={ref}
                variant={variant}
                message={message}
                icon={icon}
                description={description}
                layout={layout}
                onClose={handleClose}
            />
        );
    },
);

export type SnackbarProviderProps = PropsWithChildren<
    Omit<NotistackProviderProps, 'Components' | 'action'>
>;

/**
 * SnackbarProvider that uses the Alert-based design system.
 * Wraps notistack's SnackbarProvider with custom components for each variant.
 */
export const SnackbarProvider = (props: SnackbarProviderProps) => {
    const { children, ...rest } = props;

    return (
        <NotistackProvider
            {...rest}
            Components={{
                default: CustomSnackbar,
                success: CustomSnackbar,
                error: CustomSnackbar,
                warning: CustomSnackbar,
                info: CustomSnackbar,
            }}
        >
            {children}
        </NotistackProvider>
    );
};
